import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AdvancedFiltersPanel, DesktopFiltersPanel } from "./AdvancedFiltersPanel";
import { X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export type PeriodFilter = '7d' | '30d' | '90d';

export interface FilterState {
  category: 'all' | 'baby-food';
  categories: string[]; // Multi-select product categories (category_id)
  bacteria: string[]; // Multi-select: listeria, salmonella, ecoli
  riskType: 'all' | 'mycotoxine_aflatoxine' | 'mycotoxine_ochratoxine' | 'pesticide_eto' | 'chimique_moah_pah' | 'conditionnement';
  riskCategory: 'all' | 'pathogen' | 'chemical' | 'allergen' | 'packaging' | 'other'; // Filter by risk family
  severity: 'all' | 'high' | 'medium' | 'low';
  pregnancyOnly: boolean;
  retailers: string[]; // Multi-select retailers
  regions: string[]; // Multi-select regions (replaces single region)
  agents: string[]; // Filter by pathogen (for extended mode)
  search: string; // Text search for product name or brand
  period: PeriodFilter; // Time period filter
}

interface CompactFilterBarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  activeFiltersCount: number;
  totalCount?: number;
  isLoading?: boolean;
}

export const CompactFilterBar = ({ filters, onFiltersChange, activeFiltersCount, totalCount, isLoading }: CompactFilterBarProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [animatingBadge, setAnimatingBadge] = useState<'baby' | 'pregnancy' | null>(null);
  const isMobile = useIsMobile();

  const toggleBabyFood = () => {
    if (filters.category !== 'baby-food') {
      setAnimatingBadge('baby');
      setTimeout(() => setAnimatingBadge(null), 300);
    }
    if (filters.category === 'baby-food') {
      onFiltersChange({ ...filters, category: 'all' });
    } else {
      // Activer Bébé ET désactiver Grossesse
      onFiltersChange({ 
        ...filters, 
        category: 'baby-food',
        pregnancyOnly: false,
        agents: []
      });
    }
  };

  const togglePregnancy = () => {
    const newValue = !filters.pregnancyOnly;
    if (newValue) {
      setAnimatingBadge('pregnancy');
      setTimeout(() => setAnimatingBadge(null), 300);
      // Activer Grossesse ET désactiver Bébé
      onFiltersChange({ 
        ...filters, 
        pregnancyOnly: true,
        category: 'all',
        agents: ['listeria', 'salmonella', 'toxoplasma']
      });
    } else {
      // Désactiver Grossesse
      onFiltersChange({ 
        ...filters, 
        pregnancyOnly: false,
        agents: []
      });
    }
  };

  const clearAgents = () => {
    onFiltersChange({ ...filters, agents: [] });
  };

  const clearSearch = () => {
    onFiltersChange({ ...filters, search: '' });
  };

  const periodLabels: Record<PeriodFilter, string> = {
    '7d': '7j',
    '30d': '30j',
    '90d': '90j'
  };

  return (
    <div className="sticky top-0 z-40 bg-background border-b">
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          {/* Period selector - Desktop only */}
          {!isMobile && (
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {(['7d', '30d', '90d'] as PeriodFilter[]).map((period) => (
                <button
                  key={period}
                  onClick={() => onFiltersChange({ ...filters, period })}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                    filters.period === period
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {periodLabels[period]}
                </button>
              ))}
            </div>
          )}

          {/* Main filter toggles */}
          <div className="flex items-center gap-2 flex-1 overflow-x-auto">
            <Badge
              variant={filters.category === 'baby-food' ? "default" : "outline"}
              className={cn(
                "cursor-pointer transition-all duration-200 h-9 px-4 text-sm whitespace-nowrap",
                filters.category === 'baby-food' 
                  ? "bg-blue-500 text-white hover:bg-blue-600 border-blue-500"
                  : "hover:bg-blue-100 hover:border-blue-400 dark:hover:bg-blue-900/50 dark:hover:border-blue-600",
                animatingBadge === 'baby' && "animate-badge-pop"
              )}
              onClick={toggleBabyFood}
            >
              👶 Bébé
              {filters.category === 'baby-food' && (
                <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-medium animate-fade-in">
                  {isLoading ? '...' : totalCount}
                </span>
              )}
            </Badge>
            
            <Badge
              variant={filters.pregnancyOnly ? "default" : "outline"}
              className={cn(
                "cursor-pointer transition-all duration-200 h-9 px-4 text-sm whitespace-nowrap",
                filters.pregnancyOnly 
                  ? "bg-pink-500 text-white hover:bg-pink-600 border-pink-500"
                  : "hover:bg-pink-100 hover:border-pink-400 dark:hover:bg-pink-900/50 dark:hover:border-pink-600",
                animatingBadge === 'pregnancy' && "animate-badge-pop"
              )}
              onClick={togglePregnancy}
            >
              🤰 Grossesse
              {filters.pregnancyOnly && (
                <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-medium animate-fade-in">
                  {isLoading ? '...' : totalCount}
                </span>
              )}
            </Badge>

            {/* Search indicator badge */}
            {filters.search && filters.search.trim() !== '' && (
              <Badge
                variant="secondary"
                className="cursor-pointer transition-colors h-9 px-4 text-sm whitespace-nowrap bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 flex items-center gap-2"
                onClick={clearSearch}
              >
                🔍 "{filters.search.length > 15 ? filters.search.slice(0, 15) + '...' : filters.search}"
                <X className="h-3 w-3" />
              </Badge>
            )}

            {/* Pathogènes actifs (hors mode grossesse) */}
            {filters.agents.length > 0 && !filters.pregnancyOnly && (
              <Badge
                variant="secondary"
                className="cursor-pointer transition-colors h-9 px-4 text-sm whitespace-nowrap bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-300 flex items-center gap-2"
                onClick={clearAgents}
              >
                🦠 {filters.agents.length} pathogène{filters.agents.length > 1 ? 's' : ''}
                <X className="h-3 w-3" />
              </Badge>
            )}
          </div>
          <AdvancedFiltersPanel
            filters={filters}
            onFiltersChange={onFiltersChange}
            activeFiltersCount={activeFiltersCount}
            isOpen={filtersOpen}
            onOpenChange={setFiltersOpen}
          />
        </div>
        
        {/* Desktop: Collapsible panel under the bar */}
        {!isMobile && (
          <DesktopFiltersPanel
            filters={filters}
            onFiltersChange={onFiltersChange}
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
          />
        )}
      </div>
    </div>
  );
};
